import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { CheckIcon, CloseIcon, EditIcon, ScreenHeading, cx } from "../ui";
import { meetingTitle, untitledMeeting } from "./meeting-title";

/** The heading's own register, for the field that takes its place. */
const INHERIT = "[font:inherit] [letter-spacing:inherit] [text-transform:inherit] text-inherit";

/** The heading's controls are 44px targets that do not make the heading's line taller. */
const TARGET = "h-icon-button -my-2";

/**
 * The name field: the heading itself, editable (#766). The heading's type through
 * `INHERIT`, on the same borderless, rounded surface as every other text input. As wide as
 * its text (`field-sizing: content`, `size` as the fallback), at least four characters,
 * and short of the row by Save and Cancel. Focus uses the app-wide outline.
 */
const NAME_FIELD =
  // On the baseline, as tall as the heading's line, with the shared input surface treatment.
  "inline-block align-baseline m-0 px-2 h-auto border-0 rounded-control bg-surface-2 appearance-none " +
  "max-w-[calc(100%-7rem)] [field-sizing:content]";

/**
 * The heading's icon buttons -- the pencil, Save (check) and Cancel (close): a 44px square
 * with no box of its own, the glyph at the pencil's size. Each is named by its label alone.
 */
const ICON_BUTTON =
  "inline-flex size-icon-button items-center justify-center rounded-squircle bg-transparent border-none " +
  "cursor-pointer button-transition enabled:hover:opacity-80 enabled:active:button-press " +
  "disabled:opacity-45 disabled:cursor-default";

/**
 * The meeting screen's heading, and renaming it (#708, map decision 6). The web is the
 * reference the apps copy, so the behaviour here is the spec:
 *
 * - The meeting's creator (`canRename`) gets a pencil right after the name; tapping the
 *   name also edits. Everyone else gets the name only -- no pencil, and tapping does nothing.
 * - Editing swaps the name for a field at the same size, focused with its text selected
 *   (empty, with the date fallback as its placeholder, while the meeting has no name).
 *   Save and Cancel sit beside it; Enter saves, Escape cancels, and leaving the field does
 *   neither. Save is off while the text is blank, and unchanged text closes with no request.
 * - Saving shows the new name at once. If the request fails, the old name comes back, a
 *   short note says so, and the field reopens with what was typed.
 * - The field's text is set when it opens and never after, so a summary's title landing
 *   mid-edit leaves it alone. After Save the typed name wins (the server keeps it); after
 *   Cancel the heading shows the latest title.
 * - Focus returns to the pencil afterwards, and the new name is announced.
 */
export function MeetingHeading({
  sessionId,
  title,
  createdAt,
  canRename,
  onRenamed,
}: {
  sessionId: string;
  /** The latest title the server reported; empty while the meeting has no name. */
  title: string | null | undefined;
  createdAt: string | null | undefined;
  canRename: boolean;
  /** The server took the new name; the caller keeps its copy of the result in step. */
  onRenamed?: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  /**
   * The name this screen saved. Shown in place of `title` from the moment Save is pressed,
   * because a poll fetched before the rename landed would otherwise put the old name back;
   * the summary never overwrites a rename, so nothing newer can come from the server.
   */
  const [saved, setSaved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  /** Only the latest save may settle the heading: an older one's answer is stale. */
  const saveSeq = useRef(0);
  /** Select the text when the field opens; not when it reopens after a failure. */
  const selectOnOpen = useRef(true);
  const restoreFocus = useRef(false);
  const field = useRef<HTMLInputElement>(null);
  const pencil = useRef<HTMLButtonElement>(null);

  const current = (saved ?? title ?? "").trim();
  const shown = meetingTitle(current, createdAt);

  useEffect(() => {
    if (editing) {
      field.current?.focus();
      if (selectOnOpen.current) field.current?.select();
    } else if (restoreFocus.current) {
      restoreFocus.current = false;
      pencil.current?.focus();
    }
  }, [editing]);

  function open() {
    captureAnalytics("dialog_viewed", { dialog: "rename_meeting" });
    setDraft(current);
    selectOnOpen.current = true;
    setEditing(true);
  }

  function close() {
    captureAnalytics("dialog_dismissed", { dialog: "rename_meeting" });
    restoreFocus.current = true;
    setFailed(false);
    setEditing(false);
  }

  function save() {
    const typed = draft;
    const next = typed.trim();
    if (!next) return;
    if (next === current) {
      close();
      return;
    }
    const previous = saved;
    const mine = ++saveSeq.current;
    setSaved(next);
    setAnnouncement(next);
    close();
    api.renameSession(sessionId, next).then(
      (out) => {
        if (saveSeq.current !== mine) return;
        // The server cleans a name as it does a display name; show what it kept.
        const kept = typeof out?.title === "string" && out.title.trim() ? out.title.trim() : next;
        setSaved(kept);
        captureAnalytics("result_renamed", { target: "meeting" });
        onRenamed?.(kept);
      },
      () => {
        if (saveSeq.current !== mine) return;
        setSaved(previous);
        setAnnouncement("");
        setFailed(true);
        setDraft(typed);
        selectOnOpen.current = false;
        restoreFocus.current = false;
        setEditing(true);
      }
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // An input method composing a word (Hindi, Japanese) uses Enter to commit the word.
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  if (!canRename) return <ScreenHeading>{shown}</ScreenHeading>;

  return (
    <div className="flex flex-col gap-tight">
      {editing ? (
        // The same inline line as the name and its pencil, so nothing below moves: Save and
        // Cancel are the pencil's own 44px buttons (the line's height is set by them, as by
        // the pencil), side by side so their boxes do not overlap. Save is set off so the
        // ink gap from the field's end to the tick is at least the tick-to-cross gap
        // (about 35 and 31). One line; the field stops short of the row.
        <ScreenHeading className="whitespace-nowrap">
          <input
            ref={field}
            type="text"
            aria-label={t("result.meeting_name")}
            value={draft}
            maxLength={80}
            size={Math.max((draft || untitledMeeting(createdAt)).length, 4)}
            placeholder={untitledMeeting(createdAt)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            data-editing-title
            className={cx(INHERIT, NAME_FIELD)}
            style={{ minWidth: "4ch" }}
          />
          <button
            type="button"
            aria-label={t("common.save")}
            onClick={save}
            disabled={draft.trim().length === 0}
            className={cx(ICON_BUTTON, TARGET, "align-middle ml-5 text-accent")}
          >
            <CheckIcon className="size-icon" />
          </button>
          <button
            type="button"
            aria-label={t("common.cancel")}
            onClick={close}
            className={cx(ICON_BUTTON, TARGET, "align-middle text-ink")}
          >
            <CloseIcon className="size-icon" />
          </button>
        </ScreenHeading>
      ) : (
        <ScreenHeading>
          {/* A span, not a button: a browser lays a button out as one box, so a name
              that wraps would push the pencil onto a line of its own. Tapping the name is
              a shortcut for the pointer; the pencil is the control, for the keyboard and
              screen readers too. */}
          <span onClick={open} className="cursor-text">
            {shown}
          </span>
          <button
            ref={pencil}
            type="button"
            aria-label={t("result.rename")}
            onClick={open}
            className={cx(ICON_BUTTON, TARGET, "align-middle ml-1 text-accent")}
          >
            <EditIcon className="size-icon" />
          </button>
        </ScreenHeading>
      )}
      {failed ? (
        <p role="alert" className="m-0 text-note text-danger">
          {t("result.rename_failed")}
        </p>
      ) : null}
      {/* The new name, said once: the heading changing is silent to a screen reader. */}
      <span className="sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
